/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    fbq: any;
  }
}

export enum FBPixelEvent {
  VisitRegistration = 'Visit Registration', // Custom
  OpenRegForm = 'Open Registration Form', // Custom
  CompleteRegistration = 'CompleteRegistration', // Standard event
  JoinedSession = 'Joined Session', // Custom
}

/**
 * React Facebook Pixel Module
 *
 * @package react-facebook-pixel
 * @author  Zain Sajjad <zsajjad93@gmail.com>
 */

//
let initialized = false;
if (typeof window !== 'undefined') {
  initialized = Boolean(window.fbq);
}
let debug = false;

/**
 * Utilities
 */

/**
 * Warn
 * @param  {...any} args
 */
const warn = (...args: any[]): void => {
  if (!debug) {
    return;
  }
  // eslint-disable-next-line no-console
  console.info(...['[react-facebook-pixel]'].concat(args));
};

/**
 * Log
 * @param  {...any} args
 */
const log = (...args: any[]): void => {
  if (!debug) {
    return;
  }
  // eslint-disable-next-line no-console
  console.info(...['[react-facebook-pixel]'].concat(args));
};

const verifyInit = (): boolean => {
  if (!initialized) {
    warn('Pixel not initialized before using call FBPixel.init with required params');
  }
  return initialized;
};

const injectFBPixelScript = (): void => {
  if (typeof window === 'undefined') return;

  /* eslint-disable  */
  void (function (f: any, b: any, e: any, v: any) {
    if (f.fbq) return;
    let n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    let t: any = b.createElement(e);
    t.async = !0;
    t.src = v;
    let s: any = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable  */
};

//
const defaultOptions = {
  autoConfig: true,
  debug: false,
};

//
const FBPixel = {
  injectFBPixelScript,
  init(pixelId: string, advancedMatching = {}, options = defaultOptions): void {
    injectFBPixelScript();

    if (!pixelId) {
      warn('Please insert pixel id for initializing');
    } else {
      if (options.autoConfig === false) {
        window.fbq('set', 'autoConfig', false, pixelId); // eslint-disable-line no-undef
      }

      window.fbq('init', pixelId, advancedMatching); // eslint-disable-line no-undef
      window.fbq.disablePushState = true; // eslint-disable-line no-undef
      initialized = true;
      debug = options.debug;
    }
  },

  pageView(): void {
    if (!verifyInit()) {
      return;
    }

    window.fbq('track', 'PageView'); // eslint-disable-line no-undef

    if (debug) {
      log("called fbq('track', 'PageView');");
    }
  },

  async trackCompleteRegistration(pixelId?: string | null, email?: string | null): Promise<void> {
    if (!pixelId) return;
    try {
      await new Promise<void>((rs, rj) => {
        try {
          const pixelImageUrl = `https://www.facebook.com/tr?id=${pixelId}&ev=${
            FBPixelEvent.CompleteRegistration
          }&cd[email]=${encodeURIComponent(email ?? '')}`;
          const img = new Image();
          img.onload = (): void => rs();
          img.onerror = (e): void => rj(e);
          img.src = pixelImageUrl;
        } catch (e) {
          rj(e);
        }
      });
    } catch (e) {
      console.error(`FBPixel: trackCompleteRegistration failed with pixelId: ${pixelId}`, e);
    }
  },

  /* eslint-disable @typescript-eslint/no-explicit-any */
  track(title: string, data: any): void {
    if (!verifyInit()) {
      return;
    }

    window.fbq('track', title, data); // eslint-disable-line no-undef

    if (debug) {
      log(`called fbq('track', '${title}');`);

      if (data) {
        log('with data', data);
      }
    }
  },

  trackSingle(pixel: string, title: string, data: any): void {
    if (!verifyInit()) {
      return;
    }

    window.fbq('trackSingle', pixel, title, data); // eslint-disable-line no-undef

    if (debug) {
      log(`called fbq('trackSingle', '${pixel}', '${title}');`);

      if (data) {
        log('with data', data);
      }
    }
  },

  trackCustom(event: string, data: any): void {
    if (!verifyInit()) {
      return;
    }

    window.fbq('trackCustom', event, data); // eslint-disable-line no-undef

    if (debug) {
      log(`called fbq('trackCustom', '${event}');`);

      if (data) {
        log('with data', data);
      }
    }
  },

  trackSingleCustom(pixel: string, event: string, data: any): void {
    if (!verifyInit()) {
      return;
    }

    window.fbq('trackSingle', pixel, event, data); // eslint-disable-line no-undef

    if (debug) {
      log(`called fbq('trackSingleCustom', '${pixel}', '${event}');`);

      if (data) {
        log('with data', data);
      }
    }
  },

  grantConsent(): void {
    if (!verifyInit()) {
      return;
    }

    window.fbq('consent', 'grant');

    if (debug) {
      log(`called fbq('consent', 'grant');`);
    }
  },

  revokeConsent(): void {
    if (!verifyInit()) {
      return;
    }

    window.fbq('consent', 'revoke');

    if (debug) {
      log(`called fbq('consent', 'revoke');`);
    }
  },

  fbq(...args: any[]): void {
    if (!verifyInit()) {
      return;
    }

    window.fbq(...args); // eslint-disable-line no-undef

    if (debug) {
      log(`called fbq('${args.slice(0, 2).join("', '")}')`);

      if (args[2]) {
        log('with data', args[2]);
      }
    }
  },
};

export default FBPixel;
